/**
 * Jordan National Identification Number (NID) format validator.
 *
 * Phase 03 Plan 03-06 Task 1 (EMP-07). Backend owns authoritative uniqueness
 * + check-digit verification; the frontend schema is FORMAT ONLY — we gate
 * "shape looks like a Jordan NID" so the user can't submit obviously invalid
 * input, but silent-breakage-expensive correctness (duplicate detection,
 * MoI-registry-backed check digit) is an RFC 7807 response from the server.
 *
 * Format contract (03-RESEARCH §D-10):
 *   - Exactly 10 digits
 *   - Leading digit MUST be 1-9 (no leading zero — padding that way is an
 *     ambiguity source when the value is typed into a numeric field or a
 *     spreadsheet auto-strips leading zeros)
 *
 * Rejects: non-numeric, 9-digit, 11-digit, leading-zero 10-digit.
 *
 * MUST-test per project conventions §Testing Philosophy — ID validators are
 * data-integrity critical (a misvalidated NID leaks into SSC filings).
 */
import { z } from "zod";

/** `^[1-9]\d{9}$` — 10 digits, first digit non-zero. */
export const JORDAN_NID_PATTERN = /^[1-9]\d{9}$/;

export const jordanNidSchema = z.string().regex(JORDAN_NID_PATTERN, {
  message: "Jordan NID must be exactly 10 digits with no leading zero",
});

export type JordanNid = z.infer<typeof jordanNidSchema>;
